import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { Search, Loader2, MessageSquare, CheckCircle, Clock } from "lucide-react";
import { toast } from "sonner";

interface Ticket {
    id: string;
    user_id: string;
    email: string;
    subject: string;
    message: string;
    status: string;
    created_at: string;
}

const SupportManagement = () => {
    const { t } = useLanguage();
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        fetchTickets();
    }, []);

    const fetchTickets = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from("support_tickets" as any)
                .select("*")
                .order("created_at", { ascending: false });

            if (error) throw error;
            setTickets((data as any) || []);
        } catch (error: any) {
            console.error("Error fetching tickets:", error);
            // Don't toast error if table doesn't exist yet, just show empty
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (id: string, newStatus: string) => {
        try {
            const { error } = await supabase
                .from("support_tickets" as any)
                .update({ status: newStatus })
                .eq("id", id);

            if (error) throw error;
            toast.success(t("อัพเดทสถานะสำเร็จ", "Status updated successfully"));
            fetchTickets();
        } catch (error: any) {
            toast.error(error.message);
        }
    };

    const filteredTickets = tickets.filter(ticket =>
        ticket.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.message.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex justify-center p-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                    <MessageSquare className="h-6 w-6 text-primary" />
                    {t("จัดการคำขอรับความช่วยเหลือ", "Support Management")}
                </h2>
                <div className="relative w-full md:w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder={t("ค้นหา...", "Search...")}
                        className="pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="grid gap-4">
                {filteredTickets.length === 0 ? (
                    <Card>
                        <CardContent className="p-12 text-center text-muted-foreground">
                            {t("ไม่พบข้อมูล", "No tickets found")}
                        </CardContent>
                    </Card>
                ) : (
                    filteredTickets.map((ticket) => (
                        <Card key={ticket.id} className="overflow-hidden">
                            <div className="flex flex-col md:flex-row">
                                <div className="flex-1 p-6 space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Badge variant={ticket.status === "pending" ? "destructive" : "secondary"}>
                                                {ticket.status === "pending" ? (
                                                    <Clock className="h-3 w-3 mr-1" />
                                                ) : (
                                                    <CheckCircle className="h-3 w-3 mr-1" />
                                                )}
                                                {ticket.status === "pending" ? t("รอดำเนินการ", "Pending") : t("แก้ไขแล้ว", "Resolved")}
                                            </Badge>
                                            <span className="text-sm font-bold text-primary capitalize">{ticket.subject}</span>
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                            {new Date(ticket.created_at).toLocaleString()}
                                        </span>
                                    </div>

                                    <div>
                                        <p className="text-xs text-muted-foreground mb-1">{t("อีเมลผู้ติดต่อ", "User Email")}</p>
                                        <p className="font-medium">{ticket.email}</p>
                                    </div>

                                    <div>
                                        <p className="text-xs text-muted-foreground mb-1">{t("ข้อความ", "Message")}</p>
                                        <p className="text-sm bg-muted/50 p-3 rounded-lg border border-border/50">
                                            {ticket.message}
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-muted/30 p-6 flex flex-col justify-center gap-2 border-l border-border/50">
                                    {ticket.status === "pending" ? (
                                        <Button
                                            size="sm"
                                            className="gap-2"
                                            onClick={() => handleStatusChange(ticket.id, "resolved")}
                                        >
                                            <CheckCircle className="h-4 w-4" />
                                            {t("ทำเครื่องหมายว่าเรียบร้อย", "Mark as Resolved")}
                                        </Button>
                                    ) : (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="gap-2"
                                            onClick={() => handleStatusChange(ticket.id, "pending")}
                                        >
                                            <Clock className="h-4 w-4" />
                                            {t("เปลี่ยนเป็นรอดำเนินการ", "Mark as Pending")}
                                        </Button>
                                    )}
                                    <a href={`mailto:${ticket.email}`}>
                                        <Button variant="ghost" size="sm" className="w-full gap-2">
                                            <MessageSquare className="h-4 w-4" />
                                            {t("ส่งอีเมลตอบกลับ", "Reply via Email")}
                                        </Button>
                                    </a>
                                </div>
                            </div>
                        </Card>
                    ))
                )}
            </div>
        </div>
    );
};

export default SupportManagement;
